/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Check } from 'lucide-react';
import { useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { type HomeTask, type HomeTaskCategory } from '../../lib/api/home.js';
import { cn } from '../../lib/utils/cn.js';

const CATEGORY_TONE: Readonly<Record<HomeTaskCategory, BadgeTone>> = {
  social: 'violet',
  sensory: 'amber',
  cognitive: 'green',
  communication: 'rose',
  motor: 'slate',
  other: 'slate',
};

interface HomeTaskCardProps {
  readonly task: HomeTask;
  readonly canMark: boolean;
  readonly onMark: (id: string) => void;
}

/** One home-task card (Figma Parent → Home Tasks). Round check + category chip. */
export function HomeTaskCard({ task, canMark, onMark }: HomeTaskCardProps): JSX.Element {
  const { t } = useTranslation();
  const done = task.todayStatus === 'done';

  const handleMark = useCallback(() => {
    if (!done) {
      onMark(task.id);
    }
  }, [done, onMark, task.id]);

  return (
    <div
      className={cn(
        'flex items-start gap-4 rounded-2xl border p-4',
        done
          ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40'
          : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900',
      )}
    >
      <button
        type="button"
        onClick={handleMark}
        disabled={!canMark || done}
        aria-label={t('parent.homeTasks.markDone')}
        className={cn(
          'focus-ring mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
          done
            ? 'border-emerald-500 bg-emerald-500 text-white'
            : 'border-slate-300 text-transparent hover:border-emerald-400 dark:border-slate-600',
        )}
      >
        <Check className="h-4 w-4" aria-hidden="true" />
      </button>
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'font-medium text-slate-800 dark:text-white',
            done && 'text-slate-400 line-through dark:text-slate-500',
          )}
        >
          {task.title}
        </p>
        {task.instructions !== null && task.instructions !== '' && (
          <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{task.instructions}</p>
        )}
        <div className="mt-2 flex items-center gap-2">
          <Badge label={t(`homeCategory.${task.category}`)} tone={CATEGORY_TONE[task.category]} />
          <span
            className={cn(
              'text-xs font-medium',
              done
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-amber-600 dark:text-amber-400',
            )}
          >
            {done ? t('parent.homeTasks.completed') : t('parent.homeTasks.dueToday')}
          </span>
        </div>
      </div>
    </div>
  );
}
